import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { api } from "../api";
import { toast } from "sonner";
import { Search, Plus, Pencil, X, Save, Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";

export default function MasterManageDialog({ open, onOpenChange, kind, title, onChanged }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get(`/masters/${kind}`);
    setItems(r.data);
  };

  useEffect(() => {
    if (open) {
      load();
      setSearch("");
      setName("");
      setEditing(null);
    }
    // eslint-disable-next-line
  }, [open, kind]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/masters/${kind}`, { name: name.trim() });
      toast.success("Added");
      setName("");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/masters/${kind}/${it.id}`, { is_active: !it.is_active });
      toast.success(it.is_active ? "Marked Inactive" : "Marked Active");
      await load();
      onChanged?.();
    } catch { toast.error("Failed"); }
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    try {
      await api.put(`/masters/${kind}/${editing.id}`, { name: editName.trim() });
      toast.success("Renamed");
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const onDelete = async (it) => {
    try {
      await api.delete(`/masters/${kind}/${it.id}`);
      toast.success("Deleted");
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = items.filter((it) => it.name.toLowerCase().includes(search.toLowerCase()));
  const activeCount = items.filter((i) => i.is_active).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`master-manage-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
          <DialogDescription>
            Add, rename or deactivate values. Inactive values stay on existing records.
            <span className="ml-1 font-medium text-foreground/70">
              {activeCount}/{items.length} active.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Add new value…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            data-testid={`manage-${kind}-input`}
          />
          <Button onClick={add} disabled={busy || !name.trim()} data-testid={`manage-${kind}-add`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Add
          </Button>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search values…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid={`manage-${kind}-search`}
          />
        </div>

        <div className="border border-border rounded-lg max-h-[320px] overflow-y-auto scrollbar-thin">
          {filtered.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No matches.</div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((it) => (
                <li key={it.id} className="px-3 py-2 flex items-center gap-2 hover:bg-muted/40">
                  <div className="flex-1 min-w-0">
                    {editing?.id === it.id ? (
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                        className="h-8 text-sm"
                        autoFocus
                      />
                    ) : (
                      <span className={`text-sm truncate block ${!it.is_active ? "text-muted-foreground line-through" : ""}`} title={it.name}>
                        {it.name}
                      </span>
                    )}
                  </div>
                  <span
                    className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest border ${
                      it.is_active
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-muted text-muted-foreground border-border"
                    }`}
                  >
                    {it.is_active ? "Active" : "Inactive"}
                  </span>
                  <Switch
                    checked={it.is_active}
                    onCheckedChange={() => toggleActive(it)}
                    data-testid={`manage-${kind}-toggle-${it.id}`}
                  />
                  {editing?.id === it.id ? (
                    <>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={saveEdit}>
                        <Save className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(null)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="icon" variant="ghost" className="h-7 w-7"
                        onClick={() => { setEditing(it); setEditName(it.name); }}
                        data-testid={`manage-${kind}-edit-${it.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`manage-${kind}-delete-${it.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{it.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Permanently removes this value. Existing positions using it stay intact but the value won't be selectable again.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete(it)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
